import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { Carrier } from '@klareospace/pharos-lib-types';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@klareospace/pharos-lib-web-utils';

import { useCarrierGeneralForm } from '../hooks/use-carrier-general-form';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GeneralTabProps {
  carrier: Carrier;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function GeneralTab({ carrier }: GeneralTabProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');
  const isLocal = Boolean(carrier.spaceId);
  const { form, isDirty, isPending, onReset, onSubmit } = useCarrierGeneralForm(carrier);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{tCommon('tabs.general')}</CardTitle>
        <CardDescription>{t('spaces.settings.carriers.settings.general.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }): React.JSX.Element => (
                <FormItem>
                  <FormLabel>{tCommon('fields.name')}</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={!isLocal} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="code"
              render={({ field }): React.JSX.Element => (
                <FormItem>
                  <FormLabel>{tCommon('fields.code')}</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={!isLocal} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isLocal && isDirty && (
              <div className="flex gap-2">
                <Button
                  disabled={isPending || Object.keys(form.formState.errors).length > 0}
                  onClick={form.handleSubmit(onSubmit)}
                  type="button"
                >
                  {tCommon('actions.saveChanges')}
                </Button>
                <Button onClick={onReset} type="button" variant="outline">
                  {tCommon('actions.cancel')}
                </Button>
              </div>
            )}
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
